import { useEffect, useState, type ReactNode } from "react";
import { X } from "lucide-react";

import { useT } from "../../i18n";
import { useLocaleStore } from "../../store/localeStore";
import type { Dictionary } from "../../i18n/ru";
import type { Aoi, Episode, Plume } from "../../data/types";
import { api } from "../../services/api";
import { exportEpisode, type ExportFormat } from "../../services/export";
import { cx } from "../../lib/cx";
import { useFormat } from "../../lib/format";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { useSelectionStore } from "../../store/selectionStore";
import { useTimelineStore } from "../../store/timelineStore";
import { DetectionsTable } from "./DetectionsTable";
import { EmissionChart } from "./EmissionChart";

const parseDay = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
/** Category → sector label; falls back to the raw value if unmapped. */
const sectorLabel = (t: Dictionary, category: string) =>
  (t.sectorNames as Record<string, string>)[category] ?? category;

interface EpisodeData {
  episode: Episode;
  plumes: Plume[];
  aoi: Aoi | null;
}
interface SourceData {
  aoi: Aoi;
  episodes: Episode[];
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <dt className="shrink-0 text-text-tertiary">{label}</dt>
      <dd className="text-right text-text-primary">{children}</dd>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
      {children}
    </h3>
  );
}

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="rounded-lg bg-surface-sunken px-2 py-1.5 text-center">
      <div className="data-value text-lg font-medium leading-none text-text-primary">{value}</div>
      <div className="mt-0.5 text-xs text-text-tertiary">{label}</div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3 p-4">
      <div className="h-5 w-2/3 rounded bg-surface-sunken" />
      <div className="grid grid-cols-4 gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-11 rounded-lg bg-surface-sunken" />
        ))}
      </div>
      <div className="h-16 rounded bg-surface-sunken" />
      <div className="h-28 rounded bg-surface-sunken" />
      <div className="h-40 rounded bg-surface-sunken" />
    </div>
  );
}

function EpisodeView({ data }: { data: EpisodeData }) {
  const t = useT();
  const locale = useLocaleStore((s) => s.locale);
  const fmt = useFormat();
  const { episode, plumes, aoi } = data;
  const cursor = useTimelineStore((s) => s.cursor);
  const setCursor = useTimelineStore((s) => s.setCursor);

  const activeDate = new Date(cursor).toISOString().slice(0, 10);
  const [lng, lat] = episode.centroid;

  return (
    <div className="p-4">
      <div className="data-value text-sm text-text-secondary">
        {fmt.date((episode.first_seen).slice(0, 10))} — {fmt.date((episode.last_seen).slice(0, 10))}
      </div>

      <div className="mt-3 grid grid-cols-4 gap-1.5">
        <Stat value={Math.round(episode.duration_days)} label={t.detail.durationDays} />
        <Stat value={episode.n_detections} label={t.detail.detections} />
        <Stat value={episode.n_dates} label={t.detail.obsDates} />
        <Stat
          value={episode.max_rate_kg_hr === null ? "—" : fmt.int(episode.max_rate_kg_hr)}
          label={t.detail.maxRate}
        />
      </div>

      <p className="mt-3 rounded-lg border border-border-subtle bg-surface-sunken/60 p-2 text-xs leading-snug text-text-secondary">
        {t.detail.silenceCaveat}
      </p>

      {episode.possibly_merged && (
        <p className="mt-2 rounded-lg border border-status-warning/30 bg-status-warningBg p-2 text-xs leading-snug text-status-warning">
          {t.detail.mergedWarn}
        </p>
      )}

      <SectionTitle>{t.detail.attributes}</SectionTitle>
      <dl>
        <Row label={t.detail.sensors}>{episode.sensors.join(", ")}</Row>
        <Row label={t.detail.meanRate}>
          {episode.mean_rate_kg_hr === null ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            <span className="data-value">{fmt.int(episode.mean_rate_kg_hr)} {t.detail.kgh}</span>
          )}
        </Row>
        <Row label={t.detail.extent}>
          <span className="data-value">{episode.extent_km.toFixed(1)} {t.detail.km}</span>
        </Row>
        <Row label={t.detail.totalMass}>
          {episode.total_ch4_kg === null ? (
            <span className="text-text-tertiary">{t.detail.notCalculated}</span>
          ) : (
            <span className="data-value">{fmt.int(episode.total_ch4_kg)} {t.detail.kg}</span>
          )}
        </Row>
        <Row label={t.detail.gapDays}>
          <span className="data-value">{episode.gap_days} {t.detail.durationDays}</span>
        </Row>
        <Row label={t.detail.coordinates}>
          <span className="data-value">
            {lat.toFixed(4)}, {lng.toFixed(4)}
          </span>
        </Row>
        <Row label={t.detail.source}>
          {aoi ? aoi.name : <span className="text-text-tertiary">{t.detail.noSource}</span>}
        </Row>
        {aoi && <Row label={t.detail.sector}>{sectorLabel(t, aoi.category)}</Row>}
      </dl>

      <SectionTitle>{t.detail.chartTitle}</SectionTitle>
      <EmissionChart plumes={plumes} />

      <SectionTitle>{t.detail.rasterTitle}</SectionTitle>
      <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-border-default text-xs text-text-tertiary">
        {t.detail.rasterEmpty}
      </div>

      <SectionTitle>{t.detail.tableTitle}</SectionTitle>
      <DetectionsTable
        plumes={plumes}
        activeDate={activeDate}
        onPickDate={(date) => setCursor(parseDay(date))}
      />

      <div className="mt-4">
        <div className="mb-1 text-xs text-text-tertiary">{t.detail.download}</div>
        <div className="flex gap-2">
          {(
            [
              ["csv", "CSV"],
              ["geojson", "GeoJSON"],
              ["xlsx", "XLSX"],
            ] as [ExportFormat, string][]
          ).map(([fmt, label]) => (
            <button
              key={fmt}
              type="button"
              onClick={() => void exportEpisode(String(episode.episode_id), fmt, locale)}
              className="flex-1 rounded-lg border border-border-default px-2 py-1.5 text-sm text-text-secondary transition-colors hover:border-accent-border hover:text-accent"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SourceView({ data }: { data: SourceData }) {
  const t = useT();
  const fmt = useFormat();
  const { aoi, episodes } = data;
  const selectEpisode = useSelectionStore((s) => s.selectEpisode);
  const [lng, lat] = aoi.centroid;

  return (
    <div className="p-4">
      <div className="text-base font-semibold text-text-primary">{aoi.name}</div>

      <SectionTitle>{t.detail.attributes}</SectionTitle>
      <dl>
        <Row label={t.detail.sector}>{sectorLabel(t, aoi.category)}</Row>
        <Row label={t.detail.coordinates}>
          <span className="data-value">
            {lat.toFixed(4)}, {lng.toFixed(4)}
          </span>
        </Row>
      </dl>

      <SectionTitle>{t.detail.sourceEpisodes}</SectionTitle>
      {episodes.length === 0 ? (
        <p className="text-xs text-text-tertiary">{t.detail.noEpisodes}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {episodes.map((e) => (
            <li key={e.episode_id}>
              <button
                type="button"
                onClick={() => selectEpisode(String(e.episode_id))}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-border-subtle px-2 py-1.5 text-left text-sm transition-colors hover:border-accent-border hover:bg-accent-subtle"
              >
                <span className="text-text-primary">
                  {t.detail.episode} #{e.episode_id}
                </span>
                <span className="data-value text-xs text-text-tertiary">
                  {fmt.date((e.first_seen).slice(0, 10))} ·{" "}
                  {e.max_rate_kg_hr === null ? "—" : fmt.int(e.max_rate_kg_hr)} {t.detail.kgh}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DetailPanel() {
  const t = useT();
  const episodeId = useSelectionStore((s) => s.selectedEpisodeId);
  const sourceId = useSelectionStore((s) => s.selectedSourceId);
  const clear = useSelectionStore((s) => s.clear);
  const reduced = useReducedMotion();

  const [episodeData, setEpisodeData] = useState<EpisodeData | null>(null);
  const [sourceData, setSourceData] = useState<SourceData | null>(null);
  const [loading, setLoading] = useState(false);

  const open = episodeId !== null || sourceId !== null;

  useEffect(() => {
    let cancelled = false;
    if (episodeId) {
      setLoading(true);
      setSourceData(null);
      void api.getEpisode(episodeId).then((d) => {
        if (cancelled) return;
        setEpisodeData(d);
        setLoading(false);
      });
    } else if (sourceId) {
      setLoading(true);
      setEpisodeData(null);
      void api.getSource(sourceId).then((d) => {
        if (cancelled) return;
        setSourceData(d);
        setLoading(false);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [episodeId, sourceId]);

  const title = episodeId
    ? `${t.detail.episode} #${episodeId}`
    : sourceData?.aoi.name ?? "";

  return (
    <aside
      role="dialog"
      data-map-overlay="true"
      aria-label={title || t.detail.episode}
      aria-hidden={!open}
      className={cx(
        "absolute z-40 flex flex-col border-border-subtle bg-surface-raised shadow-lg",
        "inset-x-0 bottom-0 max-h-[80vh] rounded-t-panel border-t",
        "md:inset-x-auto md:right-0 md:top-[4.25rem] md:max-h-none md:w-[380px] md:max-w-[90vw] md:rounded-t-none md:rounded-tl-panel md:border-l",
        reduced ? "" : "transition-transform duration-200",
        open
          ? "translate-y-0 md:translate-x-0"
          : "pointer-events-none translate-y-full md:translate-y-0 md:translate-x-[110%]",
      )}
    >
      <header className="flex items-center justify-between border-b border-border-subtle px-4 py-2">
        <span className="text-sm font-semibold text-text-primary">{title}</span>
        <button
          type="button"
          aria-label={t.detail.close}
          title={t.detail.close}
          onClick={clear}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-text-tertiary transition-colors hover:bg-surface-sunken hover:text-text-primary"
        >
          <X size={16} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <Skeleton />
        ) : episodeId && episodeData ? (
          <EpisodeView data={episodeData} />
        ) : sourceId && sourceData ? (
          <SourceView data={sourceData} />
        ) : episodeId && episodeData === null && !loading ? (
          <p className="p-4 text-sm text-text-tertiary">{t.detail.notFound}</p>
        ) : null}
      </div>
    </aside>
  );
}
